<script setup lang="ts">
import { computed } from 'vue';
import { IssueMarker, MaintenanceKind, PreemptiveMaintenanceMarker } from '@/components/MaintenanceMap/types';

const props = defineProps<{
  marker: IssueMarker | PreemptiveMaintenanceMarker
}>();

const common_info = computed(() => ({
  name: props.marker.name,
  address: props.marker.address,
  date: props.marker.date,
  agency: props.marker.agency,
  contract: props.marker.contract_type,
  location: props.marker.location,
}));

const specific_info = computed(() => {
  if (props.marker.kind === MaintenanceKind.issue) {
    return {
      issue_title: props.marker.issue_title,
      issue_how_to_solve: props.marker.issue_how_to_solve
    };
  } else {
    return {
      lat_preventive_date: props.marker.last_preventive_date,
      number_of_cs: props.marker.number_of_cs,
      how_approach_client: props.marker.how_approach_client
    };
  }
});

const complete_info = computed(() => ({ ...common_info.value, ...specific_info.value }));

const link_to_maps = computed(() => {
  const { lat, lng } = complete_info.value.location;
  return `https://www.google.com/maps/place/${lat},${lng}`;
});

const link_domaintenance = computed(() => `/domaintenance/${props.marker.park}/`);
const link_issue = computed(() => {
  if (props.marker.kind === MaintenanceKind.issue) {
    return `/issuepanel/${props.marker.issue_id}/`;
  }
  return `/issuepanel/`;
});

const date_shown = computed(() => {
  let date = '';
  if (props.marker.kind === MaintenanceKind.issue) {
    date = props.marker.date;
  } else {
    if (props.marker.last_preventive_date !== null) {
      date = props.marker.last_preventive_date;
    } else {
      return "Never";
    }
  }
  return new Date(date).toLocaleDateString('it');
});

</script>

<template>
  <v-container flat fluid class="ma-0 pa-0" style="text-align: center !important;">
    <v-row no-gutters class="nowrap">
      <v-col>
        <span class="small-title">{{ complete_info.name }}</span>
      </v-col>
    </v-row>
    <v-row no-gutters class="pb-2 pt-1">
      <v-col>
        <v-divider thickness="2"/>
      </v-col>
    </v-row>
    <v-row no-gutters class="pb-2 pt-1">
      <v-col>
        <v-table density="compact">
          <tbody>
          <tr>
            <td style="text-align: left; font-weight:600">
              Address:
            </td>
            <td style="text-align: right">
              {{ props.marker.address }}
            </td>
          </tr>
          <tr>
            <td style="text-align: left; font-weight:600">
              {{ props.marker.kind === MaintenanceKind.issue ? "Issue date:" : "Last preemptive date:" }}
            </td>
            <td style="text-align: right">
              {{ date_shown }}
            </td>
          </tr>
          <tr v-if="props.marker.kind === MaintenanceKind.preemptive">
            <td style="text-align: left; font-weight:600">
              {{ "Next scheduled date:" }}
            </td>
            <td style="text-align: right">
              {{ new Date(props.marker.date).toLocaleDateString("it") }}
            </td>
          </tr>
          <tr>
            <td style="text-align: left; font-weight:600">
              Agency:
            </td>
            <td style="text-align: right">
              {{ props.marker.agency }}
            </td>
          </tr>
          <tr>
            <td style="text-align: left; font-weight:600">
              Cabinet with key:
            </td>
            <td style="text-align: right">
              {{ props.marker.cabinet_key }}
            </td>
          </tr>
          <tr>
            <td style="text-align: left; font-weight:600">
              Cabinet position:
            </td>
            <td style="text-align: right">
              {{ props.marker.cabinet_position }}
            </td>
          </tr>
          <tr v-if="props.marker.kind !== MaintenanceKind.issue">
            <td style="text-align: left; font-weight:600">
              How to approach the client:
            </td>
            <td style="text-align: right">
              {{ props.marker.how_approach_client }}
            </td>
          </tr>
          <tr v-else>
            <td style="text-align: left; font-weight:600">
              Issue title:
            </td>
            <td style="text-align: right">
              {{ props.marker.issue_title }}
            </td>
          </tr>
          <tr>
            <td style="text-align:left; font-weight:600">Location:</td>
            <td style="text-align:right">
              <v-btn text="Google Maps" target="_blank" :href="link_to_maps" size="small" slim
                     prepend-icon="mdi-map-marker-circle"/>
            </td>
          </tr>
          </tbody>
        </v-table>
      </v-col>
    </v-row>
    <v-row no-gutters>
      <v-col>
        <v-btn text="New maintenance" size="small" target="_blank" :href="link_domaintenance" slim
               prepend-icon="mdi-cog"/>
      </v-col>
      <v-col>
        <v-btn text="Park issues" size="small" slim prepend-icon="mdi-exclamation-thick" target="_blank"
               :href="link_issue"/>
      </v-col>
    </v-row>
  </v-container>
</template>

<style scoped lang="scss">
@use 'vuetify/settings';

.small-title {
  font-size: settings.$card-title-font-size - 0.35rem;
  font-weight: bold;
}
</style>

<!-- vim: softtabstop=2:shiftwidth=2
-->
